import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Radio } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LAST_NOTIF_KEY, playNotificationChime } from "@/lib/push";

const POLL_MS = 30000;
const FRESH_WINDOW_MS = 15 * 60 * 1000;

/** In-app alert (chime + banner) when the admin broadcasts a live notice.
 *  Works even without push permission via polling; dedupes with the SW message. */
const LiveNotifier = () => {
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;

  useEffect(() => {
    const show = (n) => {
      if (!n?.id || localStorage.getItem(LAST_NOTIF_KEY) === n.id) return;
      localStorage.setItem(LAST_NOTIF_KEY, n.id);
      playNotificationChime();
      toast(n.title, {
        id: `live-${n.id}`,
        description: n.body,
        duration: 20000,
        icon: <Radio className="w-5 h-5 text-[var(--fi-red)]" />,
        action: {
          label: n.kind === "tv" ? "Assistir agora" : "Ouvir agora",
          onClick: () => navRef.current(n.url || "/"),
        },
        className: "fi-live-toast",
      });
    };

    const poll = async () => {
      try {
        const { data } = await api.get("/push/latest");
        const n = data?.notification;
        if (!n) return;
        const age = Date.now() - new Date(n.created_at).getTime();
        if (age > FRESH_WINDOW_MS) {
          if (!localStorage.getItem(LAST_NOTIF_KEY)) localStorage.setItem(LAST_NOTIF_KEY, n.id);
          return;
        }
        show(n);
      } catch {}
    };

    const onSWMessage = (e) => {
      if (e.data?.type === "PUSH_RECEIVED") show(e.data.payload);
    };

    poll();
    const timer = setInterval(poll, POLL_MS);
    const onVisible = () => document.visibilityState === "visible" && poll();
    document.addEventListener("visibilitychange", onVisible);
    navigator.serviceWorker?.addEventListener("message", onSWMessage);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      navigator.serviceWorker?.removeEventListener("message", onSWMessage);
    };
  }, []);

  return null;
};

export default LiveNotifier;
